// Small sprint pieces shared by the task views, the Backlog page and the sprint header.
import { useTranslation } from "react-i18next";
import { useProject, useProjects } from "@/api/hooks";
import type { EstimateUnit } from "@/api/types/EstimateUnit";
import type { Sprint } from "@/api/types/Sprint";
import type { SprintState } from "@/api/types/SprintState";
import type { TaskSummary } from "@/api/types/TaskSummary";
import { Icon } from "@/components/Icon";
import { Chip, cx } from "@/components/ui";
import { formatAmount, formatBare, formatMinutes } from "@/lib/sprints";

/** A project's estimate unit and time logging switch, from the project list every page already holds. Scrum off: neither. */
export function useProjectUnit(projectKey: string | null | undefined): { unit: EstimateUnit; timeLogging: boolean; scrum: boolean } {
  const projects = useProjects();
  const p = projects.data?.find((x) => x.key === projectKey);
  if (!p?.scrum_enabled) return { unit: "none", timeLogging: false, scrum: false };
  return { unit: p.estimate_unit, timeLogging: p.estimate_unit === "hours" && p.time_logging, scrum: true };
}

/** Whether a page shows scrum: the named project's switch (archived ones too), or on a cross-project page, any project's. */
export function useScrum(projectKey?: string | null): boolean {
  const projects = useProjects();
  const project = useProject(projectKey ?? undefined);
  return projectKey ? !!project.data?.scrum_enabled : !!projects.data?.some((p) => p.scrum_enabled);
}

export function SprintStateChip({ state }: { state: SprintState }) {
  const { t } = useTranslation();
  return (
    <Chip tone={state === "active" ? "accent" : state === "completed" ? "pass" : "neutral"} icon={state === "completed" ? "check" : undefined}>
      {t(`sprints.state.${state}`)}
    </Chip>
  );
}

/** "Done, not verified": Done, with testing that hasn't passed. */
export function NotVerifiedChip({ compact }: { compact?: boolean }) {
  const { t } = useTranslation();
  return (
    <span
      title={t("sprints.notVerifiedHint")}
      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-blocked-bg px-2 text-xs leading-5 font-semibold whitespace-nowrap text-blocked-fg"
    >
      <Icon name="alert" size={12} />
      {compact ? t("sprints.notVerifiedShort") : t("sprints.notVerified")}
    </span>
  );
}

/**
 * The inline points / time column of a row or card: points as a pill (a "?" when an estimating project's task has
 * none and `flag` asks for it), hours as "logged / estimate".
 */
export function EstimateBadge({ task, unit, flag }: { task: TaskSummary; unit: EstimateUnit; flag?: boolean }) {
  const { t } = useTranslation();
  if (unit === "none") return null;
  if (unit === "points") {
    if (task.estimate_points == null) {
      if (!flag || task.parent_id) return null;
      return (
        <span title={t("sprints.unestimated")} className="inline-flex h-5 min-w-6 items-center justify-center rounded-full bg-blocked-bg px-1.5 text-xs font-semibold text-blocked-fg">
          ?<span className="sr-only">{t("sprints.unestimated")}</span>
        </span>
      );
    }
    return (
      <span title={t("sprints.points")} className="inline-flex h-5 min-w-6 items-center justify-center rounded-full bg-surface-2 px-1.5 text-xs font-semibold tabular-nums text-text-2">
        {formatBare("points", task.estimate_points)}
        <span className="sr-only"> {t("sprints.points")}</span>
      </span>
    );
  }
  const est = task.estimate_minutes;
  if (est == null && task.logged_minutes === 0) {
    if (!flag || task.parent_id) return null;
    return (
      <span title={t("sprints.unestimated")} className="inline-flex h-5 items-center rounded-full bg-blocked-bg px-1.5 text-xs font-semibold text-blocked-fg">
        ?<span className="sr-only">{t("sprints.unestimated")}</span>
      </span>
    );
  }
  const over = est != null && task.logged_minutes > est;
  return (
    <span
      title={t("sprints.loggedOfEstimate")}
      className={cx("font-mono text-xs whitespace-nowrap tabular-nums", over ? "font-semibold text-fail-fg" : "text-muted")}
    >
      {task.logged_minutes > 0 ? formatMinutes(task.logged_minutes) : "—"}
      {est != null && ` / ${formatMinutes(est)}`}
    </span>
  );
}

/** Fullness against capacity: a bar with its numbers, and "+N over" in text and icon when it's over. */
export function CapacityMeter({ unit, total, capacity, className }: { unit: EstimateUnit; total: number; capacity: number | null; className?: string }) {
  const { t } = useTranslation();
  if (capacity == null) return <span className={cx("text-sm text-muted tabular-nums", className)}>{formatAmount(unit, total, t)}</span>;
  const over = total > capacity;
  const pct = capacity > 0 ? Math.min(100, (total / capacity) * 100) : over ? 100 : 0;
  const label = t("sprints.capacityOf", { total: formatBare(unit, total), capacity: formatAmount(unit, capacity, t) });
  return (
    <span className={cx("inline-flex items-center gap-2 text-sm whitespace-nowrap tabular-nums", className)}>
      <span className={over ? "font-semibold text-fail-fg" : "text-text-2"}>{label}</span>
      <span
        role="progressbar"
        aria-label={t("sprints.capacity")}
        aria-valuemin={0}
        aria-valuemax={capacity}
        aria-valuenow={total}
        aria-valuetext={over ? `${label}, ${t("sprints.over", { n: formatBare(unit, total - capacity) })}` : label}
        className="inline-block h-1.5 w-20 overflow-hidden rounded-full bg-surface-2"
      >
        <i className={cx("block h-full rounded-full", over ? "bg-fail-fg" : "bg-accent")} style={{ width: `${pct}%` }} />
      </span>
      {over && (
        <span className="inline-flex items-center gap-1 font-semibold text-fail-fg">
          <Icon name="alert" size={13} />
          {t("sprints.over", { n: formatBare(unit, total - capacity) })}
        </span>
      )}
    </span>
  );
}

/**
 * Sprint progress in three parts: verified done, done but not verified, remaining. With its text, e.g.
 * "18 / 24 pts · 5 not verified".
 */
export function SprintProgress({ sprint, notVerified, className }: { sprint: Sprint; notVerified: number; className?: string }) {
  const { t } = useTranslation();
  const unit = sprint.unit;
  const total = Math.max(sprint.total, 0);
  const done = Math.min(sprint.done, total);
  const nv = Math.min(notVerified, done);
  const verified = done - nv;
  const pct = (n: number) => (total > 0 ? `${(n / total) * 100}%` : "0%");
  const text = t("sprints.progressText", { done: formatBare(unit, done), total: formatAmount(unit, total, t) });
  const nvText = nv > 0 ? t("sprints.nNotVerified", { n: formatBare(unit, nv) }) : "";
  return (
    <span className={cx("inline-flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm", className)}>
      <span
        role="progressbar"
        aria-label={t("sprints.progress")}
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        aria-valuetext={[text, nvText, t("sprints.nRemaining", { n: formatBare(unit, total - done) })].filter(Boolean).join(", ")}
        className="flex h-2 w-40 overflow-hidden rounded-full bg-surface-2"
      >
        {verified > 0 && <i className="block h-full bg-pass-fg" style={{ width: pct(verified) }} />}
        {nv > 0 && <i className="block h-full border-l-2 border-surface bg-blocked-fg" style={{ width: pct(nv) }} />}
      </span>
      <span className="font-semibold tabular-nums">{text}</span>
      {nvText && <span className="font-medium text-blocked-fg">{nvText}</span>}
    </span>
  );
}

/**
 * Planning hints for a planned sprint: the forecast from recent sprints, and warnings when the planned work is over
 * it or over the capacity left after days off. Warnings only; nothing is blocked.
 */
export function PlanHints({ sprint, className, withCapacity }: { sprint: Sprint; className?: string; /** Also warn about capacity (where no meter shows it). */ withCapacity?: boolean }) {
  const { t } = useTranslation();
  const unit = sprint.unit;
  const f = sprint.forecast;
  const overForecast = !!f && sprint.total > f.value;
  const cap = sprint.effective_capacity;
  const overCapacity = !!withCapacity && cap != null && sprint.total > cap;
  if (!f && !overCapacity) return null;
  return (
    <span className={cx("inline-flex flex-wrap items-center gap-1.5", className)}>
      {f && (
        <Chip tone={overForecast ? "blocked" : "neutral"}>
          <span title={t(`sprints.forecastBasis.${f.basis}`)}>{t("sprints.forecast", { amount: formatAmount(unit, f.value, t), count: f.sprints })}</span>
        </Chip>
      )}
      {(overForecast || overCapacity) && (
        <span role="status" className="inline-flex items-center gap-1 text-sm font-semibold text-fail-fg">
          <Icon name="alert" size={13} />
          {overForecast && overCapacity
            ? t("sprints.overBoth")
            : overForecast
              ? t("sprints.overForecast", { n: formatBare(unit, sprint.total - f!.value) })
              : t("sprints.overCapacityBy", { n: formatBare(unit, sprint.total - cap!) })}
        </span>
      )}
    </span>
  );
}

/** "10 × 7/10 days = 7": a person's capacity after days off, when they have some. */
export function capacityAfterDaysOff(unit: EstimateUnit, p: Sprint["people"][number], working: number, t: (k: string, o?: Record<string, unknown>) => string): string | null {
  if (p.capacity == null || p.available_days == null || p.available_days >= working || p.effective_capacity == null) return null;
  return t("sprints.afterDaysOff", {
    capacity: formatBare(unit, p.capacity),
    available: p.available_days,
    working,
    effective: formatBare(unit, p.effective_capacity),
  });
}
